import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import OrderHistoryTable from "@/components/customer/tables/order-history-table";
import NotesDialog from "@/components/customer/dialog/notes-dialog";
import OrderDetailsDialog from "@/components/dialog/order-history/order-details-dialog";
import useDebounce from "@/hooks/useDebounce";
import Paginat from "@/components/lib/paginat";
import OnlyTableSekeleton from "@/components/skeleton/only-table-skeleton";
import { fetchCustomerOrderHistory } from "@/redux/slice/customer-slice/customer-slice";
import { fetchSingleCbdOrder } from "@/redux/slice/cbd-slice/cbd-slice";
import { formatCurrency } from "@/lib/utils";

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return formatCurrency(0, "GBP");
  }

  const numericValue =
    typeof value === "string"
      ? Number(value.replace(/[^\d.-]/g, ""))
      : Number(value);

  return formatCurrency(Number.isNaN(numericValue) ? 0 : numericValue, "GBP");
};

const capitalize = (value) => {
  if (!value) return "N/A";
  const cleaned = value.replace(/_/g, " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

function OrderHistoryTab({ customerId, restaurantId }) {
  const dispatch = useDispatch();
  const {
    orderHistories,
    orderHistoryPagination,
    orderHistoryLoading,
  } = useSelector((state) => state.customer.customer);
  const { singleOrder, singleOrderLoading } = useSelector((state) => state.cbd.cbd);

  const [searchInput, setSearchInput] = useState("");
  const [orderType, setOrderType] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [notesDialog, setNotesDialog] = useState(false);
  const [activeNote, setActiveNote] = useState("");
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);

  const debouncedSearch = useDebounce(searchInput, 500);

  useEffect(() => {
    if (!customerId || !restaurantId) return;

    dispatch(
      fetchCustomerOrderHistory({
        restaurantId,
        customerId,
        page: currentPage,
        per_page: perPage,
        search_term: debouncedSearch,
        order_type: orderType === "all" ? "" : orderType,
        sort_field: "order_id",
        sort_order: "desc",
      }),
    );
  }, [
    dispatch,
    restaurantId,
    customerId,
    currentPage,
    perPage,
    debouncedSearch,
    orderType,
  ]);

  const rows = useMemo(
    () =>
      (orderHistories || []).map((order) => ({
        id: order?.id,
        customerId: order?.customer_id || customerId,
        customer_id: order?.customer_id || customerId,
        date: order?.date || "N/A",
        orderId:
          order?.order_id !== undefined ? `Order ID: ${order.order_id}` : "Order ID: N/A",
        orderType: order?.order_type || "N/A",
        orderPrice: formatPrice(order?.order_price),
        discount: formatPrice(order?.discount),
        loyalty: order?.loyalty || "N/A",
        earnPoints: order?.earn_points ?? 0,
        notes: order?.notes || "No notes available",
        operator: order?.operator || "N/A",
        status: capitalize(order?.status),
      })),
    [orderHistories],
  );

  const handleOpenNotes = (note) => {
    setActiveNote(note);
    setNotesDialog(true);
  };

  const handleOpenOrderDetails = (order) => {
    const orderId = order?.id;
    if (!orderId) return;

    setActiveOrder(order);
    dispatch(fetchSingleCbdOrder(orderId));
    setOrderDetailsDialog(true);
  };

  return (
    <div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Input
          value={searchInput}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchInput(e.target.value);
          }}
          placeholder="Search by Order ID"
          className="max-w-[310px] bg-cblack3 text-cwhite"
        />

        <div className="flex items-center gap-4">
          <Select
            value={orderType}
            onValueChange={(value) => {
              setCurrentPage(1);
              setOrderType(value);
            }}
          >
            <SelectTrigger className="w-[240px] bg-cblack3 text-cwhite">
              <SelectValue placeholder="Filter by Order Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Filter by Order Type</SelectItem>
              <SelectItem value="pickup">Pickup</SelectItem>
              <SelectItem value="delivery">Delivery</SelectItem>
              <SelectItem value="in house">In house</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 text-sm text-cwhite">
            <span>Show</span>
            <Select
              value={String(perPage)}
              onValueChange={(value) => {
                setCurrentPage(1);
                setPerPage(Number(value));
              }}
            >
              <SelectTrigger className="w-[84px] bg-cblack3 text-cwhite">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="40">40</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {orderHistoryLoading ? (
        <OnlyTableSekeleton header={false} row={perPage} columns={11} />
      ) : (
        <OrderHistoryTable
          rows={rows}
          onOpenOrderDetails={handleOpenOrderDetails}
          onOpenNotes={handleOpenNotes}
        />
      )}

      <Paginat
        pagination={orderHistoryPagination}
        onPageChange={setCurrentPage}
        className="pt-3"
      />

      <OrderDetailsDialog
        isOpen={orderDetailsDialog}
        onClose={() => setOrderDetailsDialog(false)}
        selectedOrder={activeOrder}
      />
      <NotesDialog isOpen={notesDialog} onClose={setNotesDialog} note={activeNote} />
    </div>
  );
}

export default OrderHistoryTab;
